import {
  IonApp,
  IonRoute,
  IonRouterOutlet,
  setupIonicReact,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Toaster } from '@up2stay/up2promo-ui';
import React, { type ComponentType, type ReactNode, Suspense } from 'react';
import { createPortal } from 'react-dom';
import type { ElementType } from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';

import { withAuth } from '@/core/auth/with-auth';
import { AppShell } from '@/core/components/app-shell';
import { ErrorBoundary } from '@/core/components/error-boundary';
import { LinkResolveDialog } from '@/core/components/link-resolve/link-resolve-dialog';
import { NativeTopEdgeBlur } from '@/core/components/native-top-edge-blur';
import { SidebarProvider } from '@/core/components/navigation/sidebar';
import { WorkspaceBootstrapGate } from '@/core/components/workspace-bootstrap-gate';
import { GuidanceProvider } from '@/core/guidance/guidance-provider';
import { useAuthSession } from '@/core/hooks/use-auth-session';
import { DeepLinkListener } from '@/core/hooks/use-deep-link';
import { useDictionaryLoader } from '@/core/hooks/use-dictionary-loader';
import { useIosSwipeBackScrollLock } from '@/core/hooks/use-ios-swipe-back-scroll-lock';
import { useLocale } from '@/core/hooks/use-locale';
import { usePosthogIdentify } from '@/core/hooks/use-posthog-identify';
import { AppUiLabelsProvider } from '@/core/providers/app-ui-labels-provider';
import { DictionaryProvider } from '@/core/providers/dictionary-provider';
import { UpgradeRequiredProvider } from '@/core/providers/upgrade-required-provider';
import { shellPaths, TABS_PREFIX } from '@/core/router/app-paths';
import { AppShellTabsOutlet } from '@/core/router/app-shell-tabs-outlet';
import {
  isAppSessionActive,
  isAuthRoutePending,
  isShellTreePath,
  persistAuthReturnUrl,
  shouldRenderAppShellTree,
} from '@/core/router/auth-route-gate';
import { withProtectedRoute } from '@/core/router/protected-route';
import { useAuthStore } from '@/core/store/auth.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { AcceptInvitationPage } from '@/routes/app/AcceptInvitationPage';
import { ClaimVerifyPage } from '@/routes/app/ClaimVerifyPage';
import { ErrorPage } from '@/routes/app/ErrorPage';
import { OnboardingPage } from '@/routes/app/OnboardingPage';
import { RejectInvitationPage } from '@/routes/app/RejectInvitationPage';
import { CompletePage } from '@/routes/auth/CompletePage';
import { LoginPage } from '@/routes/auth/LoginPage';
import { RegisterPage } from '@/routes/auth/RegisterPage';
import { VerifyPage } from '@/routes/auth/VerifyPage';
import { WelcomePage } from '@/routes/auth/WelcomePage';
import { LinkRedirectErrorPage } from '@/routes/LinkRedirectErrorPage';

const authRedirectOpts = { redirectIfAuthenticated: true } as const;
const ProtectedWelcomePage = withAuth(WelcomePage, authRedirectOpts);
const ProtectedLoginPage = withAuth(LoginPage, authRedirectOpts);
const ProtectedRegisterPage = withAuth(RegisterPage, authRedirectOpts);
const ProtectedVerifyPage = withAuth(VerifyPage, authRedirectOpts);
const ProtectedCompletePage = withAuth(CompletePage, authRedirectOpts);
const ProtectedOnboardingPage = withProtectedRoute(OnboardingPage);
const ProtectedAcceptInvitationPage = withProtectedRoute(AcceptInvitationPage);
const ProtectedRejectInvitationPage = withProtectedRoute(RejectInvitationPage);
const ProtectedErrorPage = withProtectedRoute(ErrorPage);

let ionicInitialized = false;
if (!ionicInitialized) {
  setupIonicReact({
    swipeBackEnabled: true,
  });
  ionicInitialized = true;
}

function authIonRoute(
  path: string,
  // Auth pages carry heterogeneous prop shapes once wrapped by withAuth;
  // IonRoute mounts the element without injecting RR5 route props.
  Component: ElementType,
): React.ReactElement {
  const RouteComponent = Component as ComponentType;
  return <IonRoute key={path} path={path} element={<RouteComponent />} />;
}

function DictionaryProviderInner({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const dictionary = useDictionaryLoader();

  return (
    <DictionaryProvider dictionary={dictionary} locale={locale}>
      <AppUiLabelsProvider>
        <UpgradeRequiredProvider />
        <DeepLinkListener />
        {/* Always mounted — Cap scans must work on auth screens too, not only AppShell. */}
        <LinkResolveDialog />
        {children}
        {/* Portal keeps toasts above IonApp's stacking context (as before)
            while still reading the translated labels. */}
        {createPortal(<Toaster />, document.body)}
      </AppUiLabelsProvider>
    </DictionaryProvider>
  );
}

function DictionaryWrapper({ children }: { children: ReactNode }) {
  useAuthSession();
  useIosSwipeBackScrollLock();
  usePosthogIdentify();

  return (
    <Suspense fallback={null}>
      <DictionaryProviderInner>{children}</DictionaryProviderInner>
    </Suspense>
  );
}

/**
 * Bootstraps auth + workspace state for the in-shell branch only. Auth
 * and onboarding pages don't need workspace state (and shouldn't trigger
 * redirects to /onboarding from inside themselves).
 */
function ShellEntry() {
  const usesSidebar = !isCapacitorNative();

  return (
    <SidebarProvider
      defaultOpen={usesSidebar}
      // Studio canvas under floating sidebar gutters (matches SidebarInset).
      className="app-gradient-bg"
    >
      {/*
        Gate must stay *inside* AppShell → SidebarInset. Wrapping AppShell with a
        flex-col container stacks sidebar above main (desktop “under sidebar”).
        Cap create-first flash is handled by no body-portal CTA + fixed overlay.
      */}
      <AppShell>
        <WorkspaceBootstrapGate>
          <GuidanceProvider>
            <AppShellTabsOutlet />
          </GuidanceProvider>
        </WorkspaceBootstrapGate>
      </AppShell>
    </SidebarProvider>
  );
}

function RootRedirect() {
  const authStatus = useAuthStore((state) => state.authStatus);
  const hydrated = useAuthStore((state) => state.hydrated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const isNative = isCapacitorNative();

  if (isAuthRoutePending(hydrated, authStatus, accessToken)) {
    return null;
  }

  if (isAppSessionActive(authStatus)) {
    return <Navigate to={shellPaths.home} replace />;
  }

  if (isNative) {
    return <Navigate to="/auth/welcome" replace />;
  }

  return <Navigate to="/auth" replace />;
}

/**
 * Private `/tabs/**` hit while the shell tree is not mounted (logged out).
 * Never mount `ShellEntry` here — that would put IonTabs back under the root
 * outlet PageManager and reintroduce post-login blank shells.
 */
function UnauthenticatedShellRedirect() {
  const location = useLocation();
  // Ionic matches this route from its own routeInfo, which can still be
  // `/tabs/home` for a frame after React Router has already moved to
  // `/onboarding`. The view stays mounted and would `<Navigate>` to `/auth`
  // from the new URL. Login then re-enters the shell, bootstrap sends the
  // zero-workspace user back to onboarding, and the cycle blows the update
  // depth. Only redirect while the live URL is still a shell path.
  if (!isShellTreePath(location.pathname)) {
    return null;
  }
  persistAuthReturnUrl(location.pathname + location.search);
  return <Navigate to="/auth" replace />;
}

function StandaloneCatchAll() {
  const authStatus = useAuthStore((state) => state.authStatus);
  const hydrated = useAuthStore((state) => state.hydrated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const location = useLocation();

  if (isAuthRoutePending(hydrated, authStatus, accessToken)) {
    return null;
  }

  if (isAppSessionActive(authStatus)) {
    return <Navigate to={shellPaths.home} replace />;
  }

  persistAuthReturnUrl(location.pathname + location.search);
  return <Navigate to="/auth" replace />;
}

function LegacyPublicProfileRedirect() {
  const { workspaceUsername, username } = useParams<{
    workspaceUsername: string;
    username: string;
  }>();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: shellPaths.leadsPublicProfilePath(
          workspaceUsername ?? '',
          username ?? '',
        ),
        search: location.search,
      }}
      replace
    />
  );
}

/**
 * Auth / onboarding / claim / invitations stay in a root `IonRouterOutlet`.
 * The authenticated shell mounts as a *sibling* (not an outlet child) so
 * Ionic page stacks exist only under `#main-tabs`.
 */
function AppNavigationTree() {
  const authStatus = useAuthStore((state) => state.authStatus);
  const hydrated = useAuthStore((state) => state.hydrated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const location = useLocation();

  if (isAuthRoutePending(hydrated, authStatus, accessToken)) {
    return null;
  }

  if (shouldRenderAppShellTree(authStatus, location.pathname)) {
    return <ShellEntry />;
  }

  return (
    <IonRouterOutlet>
      {/* Standalone auth stack — IonRoute keeps pages in Ionic's view stack */}
      {authIonRoute('/auth/welcome', ProtectedWelcomePage)}
      {authIonRoute('/auth', ProtectedLoginPage)}
      {authIonRoute('/auth/verify', ProtectedVerifyPage)}
      {authIonRoute('/auth/register', ProtectedRegisterPage)}
      {authIonRoute('/auth/complete', ProtectedCompletePage)}
      <Route
        path="/sign-up"
        element={<Navigate to="/auth/register" replace />}
      />
      <Route
        path="/signup"
        element={<Navigate to="/auth/register" replace />}
      />
      <Route path="/login" element={<Navigate to="/auth" replace />} />
      <Route path="/onboarding" element={<ProtectedOnboardingPage />} />
      <Route path="/claim/verify" element={<ClaimVerifyPage />} />
      <Route path="/link-error" element={<LinkRedirectErrorPage />} />
      <Route
        path="/public-profile/:workspaceUsername/:username"
        element={<LegacyPublicProfileRedirect />}
      />
      <Route
        path={`${TABS_PREFIX}/profiles/public/:workspaceUsername/:username`}
        element={<LegacyPublicProfileRedirect />}
      />
      <Route
        path="/workspace/invitation/accept"
        element={<ProtectedAcceptInvitationPage />}
      />
      <Route
        path="/workspace/invitation/reject"
        element={<ProtectedRejectInvitationPage />}
      />
      <Route path="/error" element={<ProtectedErrorPage />} />
      <Route path="/" element={<RootRedirect />} />
      <Route
        path={`${TABS_PREFIX}/*`}
        element={<UnauthenticatedShellRedirect />}
      />
      <Route path="/*" element={<StandaloneCatchAll />} />
    </IonRouterOutlet>
  );
}

/**
 * Top-level router. Standalone routes (auth, onboarding, claim, error,
 * invitation accept/reject) render in a root `IonRouterOutlet`. App shell
 * routes (`/tabs/**`) mount `IonTabs` outside that outlet so list→detail
 * stacks stay under `#main-tabs` only. Shell chrome is chosen by runtime
 * (Capacitor vs browser); `useIsMobile()` is for page layout only.
 */
export function IonicRouterSetup({
  children: _children,
}: {
  children?: ReactNode;
}) {
  return (
    <IonApp>
      {/* Outside Ionic page transforms so fixed positioning stays viewport-bound. */}
      <NativeTopEdgeBlur />
      <IonReactRouter>
        <DictionaryWrapper>
          <ErrorBoundary>
            <AppNavigationTree />
          </ErrorBoundary>
        </DictionaryWrapper>
      </IonReactRouter>
    </IonApp>
  );
}
